<script setup lang="ts">
import { useQuasar } from 'quasar';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import StandardInput from '@/components/StandardInput.vue';
import { Constants } from '@/constants';
import { getDroppedLocales } from '@/helpers/documentSubmission.helper';
import type {
  DocumentSubmissionPublishDto,
  DocumentSubmissionRefuseDto,
  DocumentSubmissionVersionDto,
  Locales,
} from '@/services/types/api.types';

const { Review, Status } = Constants.ProjectModule.DocumentSubmission;

// PROPS
const props = defineProps<{
  /** The version the history has selected, with the decisions the API accepts on it. */
  version: DocumentSubmissionVersionDto;
  canUnpublish: boolean;
  /** The locales of the version on the project page now, `null` when nothing is published. */
  publishedLocales: Locales[] | null;
  languageEnabled: Locales[];
  /** A decision is being saved: the buttons wait for it. */
  busy?: boolean;
}>();

// EMITS
const emit = defineEmits<{
  publish: [decision: DocumentSubmissionPublishDto];
  refuse: [decision: DocumentSubmissionRefuseDto];
  unpublish: [];
}>();

// CONST
const { t } = useI18n();
const $q = useQuasar();

// FORM
const { resetForm, values } = useForm<{ publishedTitle: string; note: string }>();

// REFS
const selectedLocales = ref<Locales[]>([]);

// COMPUTED
const isPending = computed(() => props.version.status === Status.Pending);
const hasDecision = computed(() => props.version.canPublish || props.version.canRefuse);
const droppedLocales = computed(() => getDroppedLocales(props.publishedLocales ?? [], selectedLocales.value));
const droppedLocalesWarning = computed(() =>
  t('project-module.documentSubmission.dialogs.decision.droppedLocales', {
    languages: droppedLocales.value.map((locale) => t(`languages.${locale}`)).join(', '),
  }),
);

// WATCH
// Every version starts from what the public sees now: the published version's languages, or this version's own when
// nothing is published, and the title the project page would show.
watch(
  () => props.version,
  (version) => {
    const preselected = props.publishedLocales ?? version.published_locales;
    selectedLocales.value = preselected.filter((locale) => props.languageEnabled.includes(locale));
    resetForm({ values: { publishedTitle: version.published_title ?? version.title, note: '' } });
  },
  { immediate: true },
);

// FUNCTIONS
// Only a review carries a note: publishing an approved version again sends no email.
const note = () => (isPending.value ? values.note?.trim() || null : null);

const onPublish = () =>
  emit('publish', {
    publishedLocales: selectedLocales.value,
    publishedTitle: values.publishedTitle?.trim() || null,
    note: note(),
  });

const onRefuse = () => emit('refuse', { note: note() });

const onUnpublish = () =>
  $q
    .dialog({
      title: t('project-module.documentSubmission.dialogs.decision.unpublishConfirm.title'),
      message: t('project-module.documentSubmission.dialogs.decision.unpublishConfirm.message'),
      cancel: true,
      persistent: true,
    })
    .onOk(() => emit('unpublish'));
</script>

<template>
  <div class="document-submission-decision-panel">
    <q-card-section v-if="hasDecision" class="document-submission-decision-panel__fields">
      <template v-if="props.version.canPublish">
        <q-field :label="t('project-module.documentSubmission.dialogs.decision.languages')" stack-label borderless>
          <template v-slot:control>
            <div class="full-width">
              <q-checkbox
                v-for="locale in props.languageEnabled"
                :key="locale"
                v-model="selectedLocales"
                :val="locale"
                :label="t(`languages.${locale}`)"
              />
            </div>
          </template>
        </q-field>
        <div v-if="droppedLocales.length" class="document-submission-decision-panel__dropped text-warning q-mb-md">
          <q-icon name="warning" class="q-mr-xs" />{{ droppedLocalesWarning }}
        </div>
        <standard-input
          name="publishedTitle"
          :label="t('project-module.documentSubmission.dialogs.decision.publishedTitle')"
          :hint="t('project-module.documentSubmission.dialogs.decision.publishedTitleHint')"
          type="text"
          maxlength="255"
          outlined
        />
      </template>
      <standard-input
        v-if="isPending"
        class="document-submission-decision-panel__note"
        name="note"
        :label="t('project-module.documentSubmission.dialogs.decision.note')"
        :hint="t('project-module.documentSubmission.dialogs.decision.noteHint')"
        type="textarea"
        :maxlength="Review.MaxNoteLength"
        counter
        autogrow
        outlined
      />
    </q-card-section>
    <q-separator v-if="hasDecision" />
    <q-card-section class="row">
      <q-card-actions>
        <q-btn
          v-if="props.version.canPublish"
          class="document-submission-decision-panel__publish"
          color="primary"
          :label="
            t(
              isPending
                ? 'project-module.documentSubmission.dialogs.decision.buttons.publish'
                : 'project-module.documentSubmission.dialogs.decision.buttons.publishAgain',
            )
          "
          :disable="!selectedLocales.length || props.busy"
          @click="onPublish"
        />
        <q-btn
          v-if="props.version.canRefuse"
          class="document-submission-decision-panel__refuse"
          color="primary"
          :label="t('project-module.documentSubmission.dialogs.decision.buttons.refuse')"
          :disable="props.busy"
          @click="onRefuse"
        />
        <q-btn
          v-if="props.canUnpublish"
          class="document-submission-decision-panel__unpublish"
          color="primary"
          outline
          :label="t('project-module.documentSubmission.dialogs.decision.buttons.unpublish')"
          :disable="props.busy"
          @click="onUnpublish"
        />
      </q-card-actions>
      <q-space />
      <q-card-actions>
        <slot />
      </q-card-actions>
    </q-card-section>
  </div>
</template>
